import type { Fen, PositionSnapshot } from '@strategy4kids/protocol';
import { CheckersAdapter, legalMovesOf } from './checkers.js';
import { parsePlacement } from './coords.js';

export interface BestMove {
  /** First move as `from+to` (final landing square), e.g. "a3c5". */
  readonly uci: string;
  /** Centipawn score from the side-to-move perspective. */
  readonly scoreCp: number;
  /** Principal variation as `from+to` moves. */
  readonly pv: string[];
}

/** The engine surface the app store and API need — ChessAI and CheckersAI both satisfy it. */
export interface MoveEngine {
  setPosition(fen: Fen): Promise<void>;
  setStrength(elo: number, opts?: { skill?: number }): Promise<void>;
  getBestMoves(n: number, opts?: { movetimeMs?: number; peek?: boolean }): Promise<BestMove[]>;
  quit(): void;
}

export interface CheckersEngineOpts {
  /** Override the elo-derived search depth (used by tests for determinism/speed). */
  readonly depth?: number;
}

export class CheckersAI implements MoveEngine {
  private readonly adapter = new CheckersAdapter();
  private pos: PositionSnapshot = this.adapter.newGame();
  private depth = 3;
  private readonly depthOverride?: number;

  constructor(opts: CheckersEngineOpts = {}) {
    this.depthOverride = opts.depth;
  }

  async setPosition(fen: Fen): Promise<void> {
    this.pos = this.adapter.fromFen(fen);
  }

  async setStrength(elo: number, _opts: { skill?: number } = {}): Promise<void> {
    this.depth = this.depthOverride ?? (elo <= 600 ? 2 : elo <= 1200 ? 3 : 4);
  }

  async getBestMoves(n: number, _opts: { movetimeMs?: number; peek?: boolean } = {}): Promise<BestMove[]> {
    // `peek` is a hint to deterministic engines (fake chess engine) to
    // answer without consuming state. CheckersAI is stateless w.r.t. its
    // caller — there's no script to drain — so we ignore it here.
    return this.#search(n, this.depthOverride ?? this.depth);
  }

  quit(): void {
    // Pure in-process engine; nothing to release.
  }

  #moves(pos: PositionSnapshot): Array<{ from: string; to: string }> {
    return legalMovesOf(parsePlacement(pos.fen), pos.turn).map((m) => ({ from: m.from, to: m.to }));
  }

  #search(n: number, depth: number): BestMove[] {
    const moves = this.#moves(this.pos);
    if (moves.length === 0) return [];
    const scored: Array<{ move: { from: string; to: string }; scoreCp: number }> = [];
    for (const m of moves) {
      const next = this.adapter.makeMove(this.pos, m.from, m.to);
      const scoreCp = -this.#negamax(next, depth - 1, -1_000_000, 1_000_000);
      scored.push({ move: m, scoreCp });
    }
    scored.sort((a, b) => b.scoreCp - a.scoreCp);
    return scored.slice(0, n).map(({ move, scoreCp }) => ({
      uci: `${move.from}${move.to}`,
      scoreCp,
      pv: [`${move.from}${move.to}`],
    }));
  }

  /** Negamax with alpha-beta. Score is from the side-to-move's perspective. */
  #negamax(pos: PositionSnapshot, depth: number, alpha: number, beta: number): number {
    const result = this.adapter.result(pos);
    if (result) {
      if ('winner' in result) return result.winner === pos.turn ? 1_000_000 : -1_000_000;
      return 0;
    }
    if (depth <= 0) {
      const ev = this.adapter.evaluate(pos);
      return pos.turn === 'w' ? ev : -ev;
    }
    const moves = this.#moves(pos);
    if (moves.length === 0) return -1_000_000;
    let best = -1_000_000;
    let alphaMax = alpha;
    for (const m of moves) {
      const score = -this.#negamax(
        this.adapter.makeMove(pos, m.from, m.to),
        depth - 1,
        -beta,
        -alpha,
      );
      if (score > best) best = score;
      if (best > alphaMax) alphaMax = best;
      if (alphaMax >= beta) break;
    }
    return best;
  }
}
